import { lazy, Suspense } from 'react';
import { AuthGuard, RoleGuard } from '@emfood/shared';
import { Outlet, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import { ROLES } from 'src/api/roles-config';
import DashboardLayout from 'src/layouts/dashboard';

import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { SeasonalCalendarFilterSchema } from 'src/sections/seasonal-calendar/search-params';

import { paths } from '../paths';
import { rootRoute } from './root';

const SeasonalCalendar = lazy(() => import('src/sections/seasonal-calendar/seasonal-calendar'));
const AddNewCalendarEntry = lazy(
  () => import('src/sections/seasonal-calendar/add-new-calendar-entry')
);
const EditSeasonalCalendarEntry = lazy(
  () => import('src/sections/seasonal-calendar/edit-calendar-entry')
);

// Seasonal Calendar routes
const seasonalCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'seasonal-calendar',
  component: () => (
    <AuthGuard
      splashScreen={<SplashScreen />}
      loginPath={paths.auth.jwt.login}
      pathOnAuthProblem={paths.auth.jwt.login}
    >
      <DashboardLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </AuthGuard>
  ),
});

const seasonalCalendarIndexRoute = createRoute({
  getParentRoute: () => seasonalCalendarRoute,
  path: '/',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    SeasonalCalendarFilterSchema.validateSync(search),
  component: () => (
    <RoleGuard
      requiredRoles={[
        ROLES.ADMIN,
        ROLES.DISTRIBUTOR_ADMIN,
        ROLES.PRODUCER_ADMIN,
        ROLES.DATA_MANAGER,
      ]}
    >
      <SeasonalCalendar />
    </RoleGuard>
  ),
});

const addNewCalendarEntryRoute = createRoute({
  getParentRoute: () => seasonalCalendarRoute,
  path: 'add',
  component: AddNewCalendarEntry,
});

const editCalendarEntryRoute = createRoute({
  getParentRoute: () => seasonalCalendarRoute,
  path: '$distributorOrgId/$distributorSiteId/$id/edit',
  component: EditSeasonalCalendarEntry,
});

export const seasonalCalendarRouteTree = seasonalCalendarRoute.addChildren([
  seasonalCalendarIndexRoute,
  addNewCalendarEntryRoute,
  editCalendarEntryRoute,
]);
